import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { pingVisit, bindVisitLifecycle } from "@/lib/visits";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { TID } from "@/constants/tid";
import { LogOut, LayoutDashboard, Compass, CreditCard, Gift, Landmark, Package, Radar, Zap, Clapperboard, Smartphone } from "lucide-react";
import BillingBanner from "@/components/BillingBanner";
import Footer from "@/components/Footer";
import DisclaimerModal from "@/components/DisclaimerModal";
import LegalAcceptModal from "@/components/LegalAcceptModal";
import { FoundersBanner } from "@/components/FoundersBanner";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { useI18n } from "@/i18n/I18nContext";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [curKey, setCurKey] = useState(0);
  useEffect(() => { const h = () => setCurKey((k) => k + 1); window.addEventListener("vsk-currency-changed", h); return () => window.removeEventListener("vsk-currency-changed", h); }, []);
  useEffect(() => { bindVisitLifecycle(); pingVisit(loc.pathname); }, [loc.pathname]);
  if (loc.pathname.startsWith("/seller/live/")) return <>{children}</>;

  const dashHref = user && user.role === "seller"
    ? "/seller"
    : user && (user.role === "admin" || user.role === "staff")
    ? "/admin"
    : "/discover";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white selection:bg-[#007AFF]/40">
      <a href="#main-content" className="vsk-skip-link">Skip to content</a>
      <FoundersBanner />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid={TID.navLogo} className="flex items-center gap-2 group">
            <span className="sr-only">VISMARKS home</span>
            <img
              src="/logo.png"
              alt="VISMARKS logo"
              data-testid="nav-logo-img"
              className="h-8 w-8 rounded-md border border-[#FF3B30]/30 object-cover"
            />
            <span className="hidden min-[360px]:inline font-black tracking-tight text-lg text-white">
              VISMARKS<span className="align-super text-[9px] font-bold ml-0.5 text-neutral-400" aria-label="trademark">™</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 text-sm ml-6">
            <Link
              to="/"
              data-testid="nav-feed"
              className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname === "/" ? "text-white" : "text-neutral-400"}`}
            >
              <span className="inline-flex items-center gap-2"><Zap className="h-4 w-4" /> {t("nav_feed", "Feed")}</span>
            </Link>
            <Link
              to="/discover"
              data-testid={TID.navDiscover}
              className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname === "/discover" ? "text-white" : "text-neutral-400"}`}
            >
              <span className="inline-flex items-center gap-2"><Compass className="h-4 w-4" /> {t("nav_discover", "Discover")}</span>
            </Link>
            <Link
              to="/stages"
              data-testid="nav-stages"
              className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname.startsWith("/stages") || loc.pathname.startsWith("/store/") ? "text-white" : "text-neutral-400"}`}
            >
              <span className="inline-flex items-center gap-2"><Clapperboard className="h-4 w-4" /> {t("nav_stages", "Stages")}</span>
            </Link>
            {!window.matchMedia("(display-mode: standalone)").matches && (
              <Link to="/app" data-testid="nav-get-app" className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname === "/app" ? "text-white" : "text-[#FF9F0A]"}`}>
                <span className="inline-flex items-center gap-2"><Smartphone className="h-4 w-4" /> Get app</span>
              </Link>
            )}
            <Link
              to="/wanted"
              data-testid="nav-wanted"
              className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname === "/wanted" || loc.pathname === "/open-demands" ? "text-white" : "text-neutral-400"}`}
            >
              <span className="inline-flex items-center gap-2"><Radar className="h-4 w-4" /> {t("nav_wanted", "Wanted")}</span>
            </Link>
            {user && user !== false && (
              <Link
                to={dashHref}
                data-testid={TID.navDashboard}
                className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname.startsWith("/seller") || loc.pathname.startsWith("/admin") ? "text-white" : "text-neutral-400"}`}
              >
                <span className="inline-flex items-center gap-2"><LayoutDashboard className="h-4 w-4" /> {t("nav_dashboard", "Dashboard")}</span>
              </Link>
            )}
            {user && user !== false && user.role === "buyer" && (
              <Link
                to="/orders"
                data-testid="nav-orders"
                className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname.startsWith("/orders") ? "text-white" : "text-neutral-400"}`}
              >
                <span className="inline-flex items-center gap-2"><Package className="h-4 w-4" /> Orders</span>
              </Link>
            )}
            {user && user !== false && (user.role === "seller" || user.role === "admin") && (
              <Link
                to="/payouts"
                data-testid="nav-payouts"
                className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname.startsWith("/payouts") ? "text-white" : "text-neutral-400"}`}
              >
                <span className="inline-flex items-center gap-2"><Landmark className="h-4 w-4" /> {t("nav_payouts", "Payouts")}</span>
              </Link>
            )}
            {user && user !== false && (user.role === "admin" || user.role === "staff") && (
              <Link
                to="/seller/stage"
                data-testid="nav-my-stage"
                className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname.startsWith("/seller/stage") ? "text-white" : "text-neutral-400"}`}
              >
                <span className="inline-flex items-center gap-2"><Clapperboard className="h-4 w-4" /> My Stage</span>
              </Link>
            )}
            {user && user !== false && user.role === "seller" && (
              <Link
                to="/billing"
                data-testid="nav-billing"
                className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname.startsWith("/billing") ? "text-white" : "text-neutral-400"}`}
              >
                <span className="inline-flex items-center gap-2"><CreditCard className="h-4 w-4" /> Billing</span>
              </Link>
            )}
            {user && user !== false && user.role !== "admin" && user.role !== "staff" && (
              <Link
                to="/referrals"
                data-testid="nav-referrals"
                className={`px-3 py-2 rounded-md hover:bg-white/5 ${loc.pathname.startsWith("/referrals") ? "text-white" : "text-neutral-400"}`}
              >
                <span className="inline-flex items-center gap-2"><Gift className="h-4 w-4" /> Refer</span>
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {user && user !== false ? (
              <>
                <CurrencySwitcher />
                <LanguageSwitcher />
                <Link to="/account" data-testid="nav-account" title="Account & security" className="hidden md:flex flex-col items-end leading-tight mr-2 rounded-md px-2 py-1 hover:bg-white/5">
                  <span className="text-xs text-neutral-400">{user.email}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#007AFF]">{user.role} · account</span>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={TID.navLogout}
                  className="text-neutral-300 hover:text-white hover:bg-white/5"
                  onClick={async () => { await logout(); nav("/"); }}
                >
                  <LogOut className="h-4 w-4 mr-1" /> {t("nav_logout", "Logout")}
                </Button>
              </>
            ) : (
              <>
                <CurrencySwitcher />
                <LanguageSwitcher />
                <Link to="/login" data-testid={TID.navLogin}>
                  <Button variant="ghost" size="sm" className="text-neutral-300 hover:bg-white/5">{t("nav_login", "Login")}</Button>
                </Link>
                <Link to="/register" data-testid={TID.navRegister}>
                  <Button size="sm" className="bg-white text-black hover:bg-neutral-200 rounded-full px-4">
                    Get started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <BillingBanner />
      <main id="main-content" key={curKey} className="pb-16 md:pb-0">{children}</main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur grid grid-cols-5 text-[10px]" data-testid="mobile-nav" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {[["/", Zap, t("nav_feed", "Feed"), "mnav-feed", (p) => p === "/"],
          ["/discover", Compass, t("nav_discover", "Discover"), "mnav-discover", (p) => p === "/discover"],
          ["/stages", Clapperboard, t("nav_stages", "Stages"), "mnav-stages", (p) => p.startsWith("/stages") || p.startsWith("/store/")],
          ["/wanted", Radar, t("nav_wanted", "Wanted"), "mnav-wanted", (p) => p === "/wanted"],
          [user && user !== false ? dashHref : "/login", LayoutDashboard, user && user !== false ? t("nav_dashboard", "Dashboard") : t("nav_login", "Sign in"), "mnav-dashboard", (p) => p.startsWith("/seller") || p.startsWith("/admin") || p === "/login"],
        ].map(([to, Icon, label, tid, isOn]) => (
          <Link key={tid} to={to} data-testid={tid} className={`flex flex-col items-center justify-center gap-0.5 py-2 ${isOn(loc.pathname) ? "text-white" : "text-neutral-500"}`}>
            <Icon className="h-5 w-5" /><span>{label}</span>
          </Link>
        ))}
      </nav>
      <Footer />
      <DisclaimerModal />
      <LegalAcceptModal />
      <ChangePasswordModal />
    </div>
  );
}
